/**
 * Token storage for chuzi-web. Bearer tokens are stored in localStorage and
 * read on every API request via the `getToken` callback in `./api.ts`.
 *
 * localStorage is a deliberate trade: we lose XSS-resistance vs. an
 * httpOnly cookie, but gain cross-domain compatibility — chuzi-web is on
 * S3+CloudFront, the API is on a different origin, and shared cookies
 * would require carefully matched domains. We treat the SPA bundle as
 * trusted and rely on standard XSS hygiene in our component code.
 */

const TOKEN_KEY = "chuzi.auth.token";

function safeStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage disabled or full — token survives only this session.
  }
}

function safeStorageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export const auth = {
  getToken(): string | null {
    return safeStorageGet(TOKEN_KEY);
  },
  setToken(token: string): void {
    safeStorageSet(TOKEN_KEY, token);
  },
  clearToken(): void {
    safeStorageRemove(TOKEN_KEY);
  },
  isAuthenticated(): boolean {
    return safeStorageGet(TOKEN_KEY) !== null;
  },
};
